// `[text](url)` and `![alt](url)`, optionally with a `"title"`, or an inline
// `<a ...>text</a>` (how Strapi's link dialog writes "open in new tab" links).
const LINK_RE =
  /!?\[([^\]]*)\]\(([^)\s]*)(?:\s+"[^"]*")?\)|<a\b[^>]*>([\s\S]*?)<\/a>/gi;

function visibleLinkLength(match: RegExpMatchArray): number {
  if (match[0].startsWith('!')) return 0;
  if (match[3] !== undefined) return match[3].replace(/<[^>]*>/g, '').length;
  return match[1].length;
}

// Closes `**` / `_` left open by the cut, innermost (latest opened) first.
function closeEmphasis(markdown: string): string {
  const plain = markdown.replace(LINK_RE, '');
  const open: { marker: string; index: number }[] = [];

  if ((plain.match(/\*\*/g) ?? []).length % 2 === 1) {
    open.push({ marker: '**', index: plain.lastIndexOf('**') });
  }
  const withoutBold = plain.replace(/\*\*/g, '  ');
  if ((withoutBold.match(/_/g) ?? []).length % 2 === 1) {
    open.push({ marker: '_', index: withoutBold.lastIndexOf('_') });
  }

  return open
    .sort((a, b) => b.index - a.index)
    .reduce((acc, { marker }) => acc + marker, markdown);
}

/**
 * Cuts markdown to roughly `limit` visible characters without splitting a
 * link: a link that starts before the limit is kept whole. Link URLs don't
 * count towards the limit, only their text.
 */
export function truncateMarkdown(
  markdown: string,
  limit: number
): { text: string; isTruncated: boolean } {
  let visible = 0;
  let pos = 0;
  let cut = -1;

  for (const match of markdown.matchAll(LINK_RE)) {
    const start = match.index ?? 0;
    const plainLength = start - pos;
    if (visible + plainLength >= limit) {
      cut = pos + (limit - visible);
      break;
    }
    visible += plainLength + visibleLinkLength(match);
    pos = start + match[0].length;
    if (visible >= limit) {
      cut = pos;
      break;
    }
  }

  if (cut === -1) {
    if (visible + (markdown.length - pos) <= limit) {
      return { text: markdown, isTruncated: false };
    }
    cut = pos + (limit - visible);
  }

  if (!markdown.slice(cut).trim()) {
    return { text: markdown, isTruncated: false };
  }

  return {
    text: closeEmphasis(markdown.slice(0, cut).trimEnd()),
    isTruncated: true,
  };
}
